import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { palette } from '../../styles/theme';
import { formatDateKey } from '@core/helpers/CalendarHelpers';
import { CustomCalendarDay } from './CustomCalendarDay';
import {
    FIXED_WEEK_COUNT,
    DEFAULT_MIN_DATE,
    TOTAL_CALENDAR_HEIGHT,
    LOCALE_MAP,
} from './calendar.constants';
import _ from 'lodash';

/**
 * Calendar - Reusable calendar component
 *
 * @param {Object} props - Component properties
 * @param {Date} props.value - Selected date (controlled)
 * @param {Date} props.defaultValue - Default date (uncontrolled)
 * @param {Function} props.onChange - Callback when selected date changes
 * @param {Function} props.onMonthChange - Callback when month changes
 * @param {Object} props.data - Data for each calendar day { 'YYYY-MM-DD': { ... } }
 * @param {Function} props.renderDay - Function to render custom day content
 * @param {Boolean} props.isLoading - Loading state
 * @param {Array} props.legends - Legends to display in top-right corner [{ color, label }]
 * @param {Object} props.calendarProps - Additional props for DateCalendar
 * @param {Object} props.dayProps - Additional props for each day
 * @param {Date} props.minDate - Minimum selectable date (defaults to 20 years back)
 * @param {Date} props.maxDate - Maximum selectable date
 */
export function Calendar ({
    value,
    defaultValue = new Date(),
    onChange = _.noop,
    onMonthChange = _.noop,
    data = {},
    renderDay,
    isLoading = false,
    legends = [],
    calendarProps = {},
    dayProps = {},
    minDate = DEFAULT_MIN_DATE,
    maxDate,
}) {
    const { t, i18n } = useTranslation();
    const currentLocale = LOCALE_MAP[i18n.language] || es;
    const [selectedDate, setSelectedDate] = useState(value || defaultValue);
    const [currentView, setCurrentView] = useState('day');

    const handleViewChange = useCallback((newView) => {
        setCurrentView(newView);
    }, []);

    const handleDateChange = useCallback((newDate) => {
        if (!newDate) return;

        if (currentView === 'year') {
            const updated = new Date(selectedDate);
            updated.setFullYear(newDate.getFullYear());
            setSelectedDate(updated);
            setCurrentView('month');
            return;
        }

        if (currentView === 'month') {
            setSelectedDate(newDate);
            setCurrentView('day');
            onChange(newDate);
            onMonthChange(newDate);
            return;
        }

        if (newDate.getMonth() !== selectedDate.getMonth() || newDate.getFullYear() !== selectedDate.getFullYear()) {
            setSelectedDate(newDate);
            onChange(newDate);
            onMonthChange(newDate);
        }
    }, [onChange, onMonthChange, selectedDate, currentView]);

    const DayComponent = useCallback((props) => {
        const { day } = props;
        const dateKey = formatDateKey(day);
        const dayData = data[dateKey];

        return (
            <CustomCalendarDay
                {...props}
                selectedDate={selectedDate}
                dayData={dayData}
                renderDay={renderDay}
                dayProps={dayProps}
            />
        );
    }, [data, selectedDate, renderDay, dayProps]);

    const legendsNode = useMemo(() => {
        if (legends.length === 0) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '1.85rem',
                    marginBottom: '0.62rem',
                    position: 'absolute',
                    top: '1.85rem',
                    right: '1.85rem',
                }}
            >
                {legends.map((legend) => (
                    <Box
                        key={legend.label}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.62rem',
                        }}
                    >
                        <Box
                            sx={{
                                width: '0.92rem',
                                height: '0.92rem',
                                borderRadius: '0.15rem',
                                backgroundColor: legend.color,
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: legend.color,
                                fontSize: '1rem',
                            }}
                        >
                            {legend.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }, [legends]);

    const handleMonthChangeInternal = useCallback((newMonth) => {
        setSelectedDate(newMonth);
        onMonthChange(newMonth);
    }, [onMonthChange]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: `${TOTAL_CALENDAR_HEIGHT}rem`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: palette.white,
                    borderRadius: '0.62rem',
                }}
            >
                <Typography>{t('loading')}</Typography>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLocale}>
            <Box className="calendar-root" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                {legendsNode}
                <DateCalendar
                    value={selectedDate}
                    onChange={handleDateChange}
                    onMonthChange={handleMonthChangeInternal}
                    view={currentView}
                    onViewChange={handleViewChange}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    slots={{
                        day: DayComponent,
                    }}
                    slotProps={{
                        day: dayProps,
                    }}
                    showDaysOutsideCurrentMonth
                    fixedWeekNumber={FIXED_WEEK_COUNT}
                    {...(minDate ? { minDate } : {})}
                    {...(maxDate ? { maxDate } : {})}
                    {...calendarProps}
                />
            </Box>
        </LocalizationProvider>
    );
}

if (process.env.NODE_ENV !== 'production') {
    Calendar.displayName = 'Calendar';
}
